import { useMemo, useState } from 'react';
import { isObject } from 'underscore';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import UploadLarge from '@ui/uploads/upload-large';
import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';
import {
  GetUploadEndpoint,
  getUploadFilename,
  UploadFileToProvider,
  SetItemContentType,
} from '@ui/uploads/validations';
import { ICONS } from '@ui/icons/types';
import type { ResolvedEmailBlock } from '../../../templates/widgets/block-types';
import type { MediaWidgetSize } from '../../../templates/widgets/widget-types';
import ColorPill from '../controls/color-pill';
import NumberField from '../controls/number-field';
import TextField from '../controls/text-field';
import SizeControl from '../controls/size-control';
import AlignmentControl from '../controls/alignment-control';
import BorderControl from '../controls/border-control';
import BorderRadiusControl from '../controls/border-radius-control';
import BoxModelControl from '../controls/box-model-control';
import { useBoxModelStyle } from '../controls/use-box-model-style';
import { useResolvedBlockStyle } from '../controls/use-resolved-block-style';
import toastTrigger from '@ui/toast/toast-trigger';
import { isIllegalImageType, renderImageWithPlayButton } from '../../../utils';

interface VideoStylePanelProps {
  block: ResolvedEmailBlock;
  onUpdateStyle: (key: string, value: unknown) => void;
  onUpdateSrc: (src: string) => void;
  onUpdateProp: (key: string, value: unknown) => void;
}

// Implementation of the Figma "Video" sidepane (media block settings).
// Mirrors LogoStylePanel: all visual overrides live on block.style; content data (src, alt, url)
// lives on block.props.
export default function VideoStylePanel({ block, onUpdateStyle, onUpdateSrc, onUpdateProp }: VideoStylePanelProps) {
  const style = block.style ?? {};
  const resolved = useResolvedBlockStyle(block.id, block.style);
  const [uploading, setUploading] = useState(false);

  const src = block.props.src as string | undefined;
  const alt = (block.props.alt as string | undefined) ?? '';
  const url = (block.props.url as string | undefined) ?? '';
  const size = (style.size as MediaWidgetSize | undefined) ?? 'original';
  const scale = (style.scale as number | undefined) ?? 100;
  const align = (style.align as string | undefined) ?? 'left';

  const padding = useBoxModelStyle({
    blockId: block.id,
    styleValue: style.padding,
    resolvedValues: [resolved.paddingTop, resolved.paddingRight, resolved.paddingBottom, resolved.paddingLeft],
    styleKey: 'padding',
    uniformity: 'paired',
    onUpdateStyle,
  });

  const handleFile = async (file: File | FileList) => {
    if (!(file instanceof File)) return;
    setUploading(true);
    try {
      if (isIllegalImageType(file)) {
        toastTrigger('Incompatible file type', { type: 'error', description: 'Only png and jpeg images are allowed' });
        return;
      }

      const withPlayButton = await renderImageWithPlayButton(file);
      if (!withPlayButton) {
        console.error('Could not render with play button');
        return;
      }
      const endpoint = await GetUploadEndpoint({ filename: getUploadFilename(file.name) });
      if (isObject(endpoint) && endpoint.url) {
        const finalUrl = await UploadFileToProvider(
          { url: endpoint.url, fields: endpoint.fields, cdnHost: endpoint?.cdnHost },
          withPlayButton
        );
        SetItemContentType({ filename: finalUrl, contentType: 'image/jpeg' }).catch(console.error);
        if (finalUrl) onUpdateSrc(finalUrl);
      }
    } catch (error) {
      console.error(error);
      handleImageUploadErrors(error);
    } finally {
      setUploading(false);
    }
  };

  const validUrl = useMemo(() => {
    try {
      if (!url) return false;
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }, [url]);

  return (
    <Container className="flex-column gap-16" padding="0 16px">
      <TextField
        label="Video URL"
        placeholder="eg: https://www.sponsor.com"
        value={url}
        onChange={(value) => onUpdateProp('url', value === '' ? undefined : value)}
        valid={!!url && validUrl}
      />
      {url && validUrl && (
        <>
          <Container width="308px" height="172px">
            <UploadLarge
              displayImageUrl={src}
              uploading={uploading}
              onFile={handleFile}
              hidePlaceholderImage
              style={{
                width: '308px',
                height: '172px',
                padding: src ? 0 : '0 24px',
                borderRadius: '16px',
                border: src ? 'none' : '1px dashed var(--global-ico-text-3)',
                backgroundColor: 'transparent',
                backgroundOrigin: 'content-box',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
              allowedFileTypes={['image/jpeg', 'image/png']}
              maxSizeInBytes={1024 * 1024 * 5}
              CustomTitle={
                <Container className="flex-column ai-center gap-4">
                  <Container className="flex ai-center gap-6">
                    <Icon name={ICONS.UPLOAD} size={12} color="var(--global-ico-text-1)" />
                    <Text className="S2" color="var(--global-ico-text-1)">
                      Upload image
                    </Text>
                  </Container>
                  <Text className="C2" color="var(--global-ico-text-3)">
                    or drag and drop here
                  </Text>
                  <Text className="C2" color="var(--global-ico-text-3)">
                    5mb | png, jpg, jpeg
                  </Text>
                </Container>
              }
            />
          </Container>

          <TextField
            label="Alt Text"
            placeholder="eg: Sponsor Banner"
            value={alt}
            onChange={(value) => onUpdateProp('alt', value === '' ? undefined : value)}
          />

          <SizeControl
            size={size}
            scale={scale}
            onChangeSize={(value) => onUpdateStyle('size', value)}
            onChangeScale={(v) => onUpdateStyle('scale', v)}
          />

          <Container className="flex gap-12">
            <ColorPill
              label="Background color"
              value={(style.background as string | undefined) ?? resolved.background}
              isOverridden={style.background !== undefined}
              onChange={(v) => onUpdateStyle('background', v)}
              onReset={() => onUpdateStyle('background', undefined)}
            />
            <AlignmentControl value={align} onChange={(value) => onUpdateStyle('align', value)} />
          </Container>

          <BorderControl
            style={style}
            onUpdateStyle={onUpdateStyle}
            resolvedColor={resolved.borderColor}
            resolvedWidth={resolved.borderWidth}
          />

          <BorderRadiusControl
            blockId={block.id}
            style={style}
            onUpdateStyle={onUpdateStyle}
            resolvedValues={[
              resolved.cornerTopLeft,
              resolved.cornerTopRight,
              resolved.cornerBottomRight,
              resolved.cornerBottomLeft,
            ]}
          />

          <BoxModelControl
            sectionLabel="Padding"
            expanded={padding.expanded}
            onToggleExpand={() => padding.setExpanded((prev) => !prev)}
            toggleIcon={ICONS.PADDING_SIDES}
            toggleLabel="padding controls"
            values={padding.values}
            onChangeSlot={padding.setSlot}
            fields={[
              { slot: 0, icon: ICONS.PADDING_TOP, ariaLabel: 'Padding top' },
              { slot: 1, icon: ICONS.PADDING_RIGHT, ariaLabel: 'Padding right' },
              { slot: 2, icon: ICONS.PADDING_BOTTOM, ariaLabel: 'Padding bottom' },
              { slot: 3, icon: ICONS.PADDING_LEFT, ariaLabel: 'Padding left' },
            ]}
            renderCollapsed={() => (
              <Container className="flex gap-12 ai-flex-end">
                <NumberField
                  label="Padding"
                  icon={ICONS.PADDING_VERTICAL}
                  allowNegative={false}
                  min={0}
                  max={100}
                  value={padding.values[0]}
                  onChange={padding.setCollapsedFirst}
                />
                <NumberField
                  icon={ICONS.PADDING_HORIZONTAL}
                  allowNegative={false}
                  min={0}
                  max={100}
                  value={padding.values[1]}
                  onChange={padding.setCollapsedSecond}
                />
              </Container>
            )}
          />
        </>
      )}
    </Container>
  );
}
